import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { canManageRecords, isAdminRole, ROLE_LABEL } from "../lib/access";
import NotificationBell from "./NotificationBell";
import FeedbackButton from "./FeedbackButton";
import {
  LayoutDashboard,
  PlusCircle,
  ListChecks,
  Users,
  Archive,
  LogOut,
  Menu,
  X,
  ShieldAlert,
  Settings,
  BellRing,
  CalendarDays,
  MessageSquareText,
  Building2,
  TriangleAlert,
} from "lucide-react";
import { Button } from "./ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/handoffs/new", label: "Create Handoff", icon: PlusCircle, testid: "nav-create-handoff" },
  { to: "/tasks", label: "Task History", icon: ListChecks, testid: "nav-tasks" },
  { to: "/schedule", label: "Schedule", icon: CalendarDays, testid: "nav-schedule" },
  { to: "/advisories", label: "Advisories", icon: TriangleAlert, testid: "nav-advisories" },
  { to: "/team", label: "Team Management", icon: Users, testid: "nav-team", adminOnly: true },
  { to: "/division-team", label: "Division Team", icon: Users, testid: "nav-division-team", supervisorOnly: true },
  { to: "/archive", label: "Archive", icon: Archive, testid: "nav-archive" },
  { to: "/preferences", label: "Notifications", icon: BellRing, testid: "nav-preferences" },
  { to: "/pilot-feedback", label: "Pilot Feedback", icon: MessageSquareText, testid: "nav-pilot-feedback", adminOnly: true, pilotFeedbackOnly: true },
  { to: "/settings", label: "Organization Settings", icon: Settings, testid: "nav-settings", adminOnly: true },
  { to: "/organizations", label: "Organizations", icon: Building2, testid: "nav-organizations", systemOwnerOnly: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-6 py-6 border-b border-slate-800">
        <img src="/watchrelay-shield.png" alt="WatchRelay" className="h-10 w-10 shrink-0 object-contain" />
        <div>
          <p className="font-display text-lg font-black leading-none text-white">Watch<span className="text-blue-400">Relay</span></p>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-1" data-testid="sidebar-org-name">{user?.org_name || "Demo Agency"}</p>
        </div>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-5">
        {NAV.filter((item) => (!item.manageOnly || canManageRecords(user)) && (!item.adminOnly || isAdminRole(user)) && (!item.supervisorOnly || user?.role === "supervisor") && (!item.pilotFeedbackOnly || user?.pilot_feedback_enabled) && (!item.systemOwnerOnly || user?.role === "system_admin")).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-blue-600 text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <item.icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-slate-800 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-700 text-xs font-bold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white" data-testid="sidebar-user-name">
              {user?.name}
            </p>
            <p className="truncate text-[11px] uppercase tracking-wider text-slate-500">
              {ROLE_LABEL[user?.role] || user?.role}
            </p>
          </div>
        </div>
        <FeedbackButton variant="sidebar" />
        <Button
          data-testid="logout-button"
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start gap-2 text-slate-400 hover:bg-slate-800 hover:text-white"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 bg-[#020617] lg:block">
        {SidebarInner}
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-950/60" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[#020617]">{SidebarInner}</aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200 bg-white px-4 lg:px-8">
          <Button
            data-testid="mobile-menu-button"
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span className="font-semibold text-slate-900">{user?.shift_name || "Operations"}</span>
            <span className="hidden sm:inline">· 24/7 Shift Handoffs</span>
          </div>
          <div className="ml-auto">
            <NotificationBell />
          </div>
        </header>
        {user?.is_demo && (
          <div
            data-testid="demo-banner"
            className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 lg:px-8"
          >
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <span>
              <span className="font-semibold">Demo environment — fictional information only.</span>{" "}
              Do not enter CAD, NCIC, medical, personnel, or other sensitive information.
            </span>
          </div>
        )}
        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
